import { useEffect, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { useTheme } from "@/lib/theme";
import { fmtRelative } from "@/lib/format";
import { FolderKanban, FileImage, FileText, Film, Globe, CheckCircle2, Clock, MessageSquare, Star, ShieldCheck } from "lucide-react";

/**
 * ClientPortal — public, no-auth read-only summary.
 * Two modes:
 *   - Shared (no `?c=`): every project + review item for the client (legacy)
 *   - Per-contact (`?c={contact_id}`): scoped to the projects that contact is
 *     assigned to as a project_member, with capability flags derived from their
 *     custom role.
 */
export default function ClientPortal() {
  const { token } = useParams();
  const [params] = useSearchParams();
  const contactId = params.get("c");
  const { branding } = useTheme();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    const qs = contactId ? `?c=${encodeURIComponent(contactId)}` : "";
    api.get(`/portal/${token}${qs}`)
      .then((r) => setData(r.data))
      .catch(() => setError("Portal not found or revoked"));
  }, [token, contactId]);

  useEffect(() => {
    if (data?.white_label && data?.primary_hsl) {
      const root = document.documentElement;
      const prev = root.style.getPropertyValue("--primary");
      root.style.setProperty("--primary", data.primary_hsl);
      return () => { root.style.setProperty("--primary", prev); };
    }
  }, [data]);

  if (error) {
    return (
      <div className="min-h-screen grid place-items-center text-sm text-muted-foreground" data-testid="portal-error">
        {error}
      </div>
    );
  }
  if (!data) {
    return (
      <div className="min-h-screen grid place-items-center text-sm text-muted-foreground" data-testid="portal-loading">
        Loading portal…
      </div>
    );
  }

  const displayName = data.white_label
    ? (data.workspace?.name || branding.platform_name)
    : branding.platform_name;
  const logo = data.white_label ? data.logo_url : null;
  const accent = data.white_label ? data.accent_text : null;
  const totalItems = data.projects.reduce((acc, p) => acc + (p.items?.length || 0), 0);
  const approvedItems = data.projects.reduce(
    (acc, p) => acc + (p.items || []).filter((it) => it.status === "approved").length,
    0
  );
  const caps = data.capabilities || {};

  return (
    <div className="min-h-screen bg-background" data-testid="portal-root">
      {/* Header */}
      <header className="border-b border-border bg-card">
        <div className="max-w-5xl mx-auto px-6 py-5 flex items-center justify-between">
          <div className="flex items-center gap-3">
            {logo ? (
              <img src={logo} alt={displayName} className="w-9 h-9 object-contain" />
            ) : (
              <div className="w-9 h-9 rounded-sm bg-primary text-primary-foreground flex items-center justify-center font-black">
                {(displayName || "C").slice(0, 1)}
              </div>
            )}
            <div>
              <div className="label-eyebrow text-[10px]">{displayName}</div>
              <h1 className="font-bold text-lg" style={{ fontFamily: "var(--font-heading)" }} data-testid="portal-client-name">
                {data.client.name}
              </h1>
              {data.contact && (
                <div className="text-[11px] text-muted-foreground mt-0.5" data-testid="portal-contact-name">
                  Signed in as <span className="font-medium text-foreground">{data.contact.name}</span>
                  {data.contact.title ? ` · ${data.contact.title}` : ""}
                </div>
              )}
            </div>
          </div>
          <div className="text-right">
            <div className="label-eyebrow text-[10px]">Last update</div>
            <div className="text-sm font-medium" data-testid="portal-last-updated">
              {data.last_updated ? fmtRelative(data.last_updated) : "—"}
            </div>
          </div>
        </div>
        {data.scoped && (
          <div className="max-w-5xl mx-auto px-6 pb-4 flex flex-wrap gap-2" data-testid="portal-capabilities">
            <CapChip on={caps.can_view} icon={ShieldCheck} label="View" testid="cap-view" />
            <CapChip on={caps.can_comment} icon={MessageSquare} label="Comment" testid="cap-comment" />
            <CapChip on={caps.can_approve} icon={CheckCircle2} label="Approve" testid="cap-approve" />
            <CapChip on={caps.can_rate} icon={Star} label="Rate" testid="cap-rate" />
          </div>
        )}
      </header>

      {/* Iter 126b — Announcements (welcome messages from launched kits) */}
      {(data.announcements || []).length > 0 && (
        <div className="max-w-5xl mx-auto px-6 pt-6 space-y-2" data-testid="portal-announcements">
          {(data.announcements || []).map((a) => (
            <div key={a.id} className="border border-primary/30 bg-primary/5 p-4 flex items-start gap-3" data-testid={`portal-announcement-${a.id}`}>
              <MessageSquare className="w-4 h-4 text-primary shrink-0 mt-1" />
              <div className="flex-1 min-w-0">
                <div className="text-sm font-semibold mb-1">{a.subject}</div>
                <div className="text-sm text-foreground/80 whitespace-pre-wrap" dangerouslySetInnerHTML={{ __html: a.body_html || "" }} />
                {a.public_url && (
                  <a href={a.public_url} target="_blank" rel="noreferrer" className="inline-block mt-2 text-xs underline text-primary" data-testid={`portal-announcement-cta-${a.id}`}>
                    Complete questionnaire →
                  </a>
                )}
              </div>
              <button
                type="button"
                onClick={async () => {
                  try {
                    await api.post(`/portal/${token}/announcements/${a.id}/dismiss`);
                    // Re-fetch payload
                    const qs = contactId ? `?c=${encodeURIComponent(contactId)}` : "";
                    api.get(`/portal/${token}${qs}`).then((r) => setData(r.data));
                  } catch { /* noop */ }
                }}
                className="text-[10px] text-muted-foreground hover:text-foreground"
                data-testid={`portal-announcement-dismiss-${a.id}`}
              >dismiss</button>
            </div>
          ))}
        </div>
      )}

      {/* Stats strip */}
      <div className="max-w-5xl mx-auto px-6 py-6 grid grid-cols-3 gap-3" data-testid="portal-stats">
        <StatBox label="Projects" value={data.projects.length} testid="portal-stat-projects" />
        <StatBox label="Review items" value={totalItems} testid="portal-stat-items" />
        <StatBox label="Approved" value={approvedItems} testid="portal-stat-approved" />
      </div>

      {/* Projects list */}
      <div className="max-w-5xl mx-auto px-6 pb-12 space-y-6">
        {data.projects.length === 0 && (
          <div className="border border-dashed border-border py-16 text-center text-muted-foreground text-sm" data-testid="portal-empty">
            {data.scoped
              ? "You haven't been assigned to any projects yet. Reach out to your account manager."
              : "No projects yet for this client."}
          </div>
        )}
        {data.projects.map((p) => (
          <section key={p.id} className="border border-border" data-testid={`portal-project-${p.id}`}>
            <header className="px-5 py-3 border-b border-border flex items-center justify-between bg-muted/30">
              <div className="flex items-center gap-2">
                <FolderKanban className="w-4 h-4 text-primary" />
                <h2 className="font-semibold" style={{ fontFamily: "var(--font-heading)" }}>{p.name}</h2>
              </div>
              <span className="text-xs text-muted-foreground">{p.items?.length || 0} items</span>
            </header>
            {p.description && (
              <p className="px-5 py-3 text-sm text-muted-foreground border-b border-border">{p.description}</p>
            )}
            {p.items.length === 0 ? (
              <div className="px-5 py-8 text-center text-sm text-muted-foreground">No items in this project yet.</div>
            ) : (
              <ul className="divide-y divide-border">
                {p.items.map((it) => (
                  <li key={it.id} className="px-5 py-3 flex items-center justify-between" data-testid={`portal-item-${it.id}`}>
                    <div className="flex items-center gap-3 min-w-0">
                      <ItemIcon t={it.item_type} />
                      <div className="min-w-0">
                        <div className="font-medium text-sm truncate">{it.title}</div>
                        <div className="text-[11px] text-muted-foreground">v{it.current_version || 1} · {it.item_type} · {fmtRelative(it.created_at)}</div>
                      </div>
                    </div>
                    <StatusChip status={it.status} approvals={it.approval_count} />
                  </li>
                ))}
              </ul>
            )}
          </section>
        ))}
      </div>

      <footer className="text-center text-[11px] text-foreground/60 py-4 border-t border-border bg-card" data-testid="portal-footer">
        {accent || `Powered by ${data.platform_name}`}
      </footer>
    </div>
  );
}

function CapChip({ on, icon: Icon, label, testid }) {
  return (
    <span
      className={`chip text-[10px] inline-flex items-center gap-1 ${on ? "bg-emerald-500/10 text-emerald-600 border-emerald-500/30" : "opacity-50"}`}
      data-testid={testid}
      title={on ? `You can ${label.toLowerCase()}` : `${label} not allowed by your role`}
    >
      <Icon className="w-3 h-3" /> {label}{on ? "" : " ✕"}
    </span>
  );
}

function StatBox({ label, value, testid }) {
  return (
    <div className="border border-border p-4" data-testid={testid}>
      <div className="label-eyebrow text-[10px] mb-1">{label}</div>
      <div className="text-3xl font-bold" style={{ fontFamily: "var(--font-heading)" }}>{value}</div>
    </div>
  );
}

function ItemIcon({ t }) {
  if (t === "image") return <FileImage className="w-4 h-4 text-muted-foreground shrink-0" />;
  if (t === "video") return <Film className="w-4 h-4 text-muted-foreground shrink-0" />;
  if (t === "website") return <Globe className="w-4 h-4 text-muted-foreground shrink-0" />;
  return <FileText className="w-4 h-4 text-muted-foreground shrink-0" />;
}

function StatusChip({ status, approvals }) {
  if (status === "approved") {
    return (
      <span className="inline-flex items-center gap-1 text-xs text-emerald-600 font-semibold whitespace-nowrap" data-testid="portal-status-approved">
        <CheckCircle2 className="w-3.5 h-3.5" /> Approved
      </span>
    );
  }
  if (status === "in_review") {
    return (
      <span className="inline-flex items-center gap-1 text-xs text-amber-600 font-semibold whitespace-nowrap">
        <Clock className="w-3.5 h-3.5" /> In review {approvals > 0 && `· ${approvals} signoff${approvals > 1 ? "s" : ""}`}
      </span>
    );
  }
  return <span className="text-xs text-muted-foreground capitalize whitespace-nowrap">{status?.replace("_", " ")}</span>;
}
